import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API = "https://booksmart-production-f6dc.up.railway.app";
const getToken = () => sessionStorage.getItem("token");
const getAccountId = () => sessionStorage.getItem("account_id");

const FONT_UI = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const S = {
  page: { fontFamily: FONT_UI, background: "#fafaf9", minHeight: "100vh", color: "#18181b", padding: "32px 16px", width: "100%", boxSizing: "border-box", overflowX: "hidden" },
  container: { maxWidth: 640, margin: "0 auto" },
  title: { fontSize: 22, fontWeight: 700, color: "#18181b", letterSpacing: "0.02em", marginBottom: 6 },
  subtitle: { fontSize: 13, color: "#52525b", marginBottom: 32, lineHeight: 1.6 },
  section: { marginBottom: 28 },
  label: { display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.02em", textTransform: "uppercase", color: "#52525b", marginBottom: 8 },
  select: { width: "100%", background: "#ffffff", border: "1px solid #e7e5e4", borderRadius: 8, padding: "10px 12px", color: "#18181b", fontSize: 14, fontFamily: FONT_UI, outline: "none", boxSizing: "border-box", WebkitAppearance: "none", appearance: "none" },
  input: { width: "100%", background: "#ffffff", border: "1px solid #e7e5e4", borderRadius: 8, padding: "10px 12px", color: "#18181b", fontSize: 14, fontFamily: FONT_UI, outline: "none", boxSizing: "border-box" },
  radioGroup: { display: "flex", flexDirection: "column", gap: 10 },
  radioRow: { display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#3f3f46" },
  radio: { accentColor: "#18181b", width: 16, height: 16, cursor: "pointer" },
  btnPrimary: { background: "#18181b", color: "#ffffff", border: "none", borderRadius: 8, padding: "12px 28px", fontWeight: 600, fontSize: 13, letterSpacing: "0.02em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", width: "100%" },
  btnDisabled: { background: "#f4f4f5", color: "#a1a1aa", border: "none", borderRadius: 8, padding: "12px 28px", fontWeight: 600, fontSize: 13, letterSpacing: "0.02em", textTransform: "uppercase", cursor: "default", fontFamily: "inherit", width: "100%" },
  error: { padding: "10px 14px", borderRadius: 8, fontSize: 12, marginBottom: 16, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca" },
  success: { padding: "24px 32px", borderRadius: 10, textAlign: "center", background: "#ffffff", border: "1px solid #e7e5e4", boxShadow: "0 1px 2px rgba(0,0,0,.04)" },
  successTitle: { fontSize: 18, fontWeight: 700, color: "#1f8a4c", marginBottom: 12 },
  successBody: { fontSize: 13, color: "#52525b", lineHeight: 1.7 },
  backLink: { background: "none", border: "1px solid #e7e5e4", color: "#52525b", borderRadius: 8, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em", textTransform: "uppercase", marginBottom: 24 },
};

const TMS_OPTIONS = [
  { value: "cargowise", label: "CargoWise" },
  { value: "gofreight", label: "GoFreight" },
  { value: "magaya", label: "Magaya" },
  { value: "descartes", label: "Descartes" },
  { value: "logisys", label: "Logi-Sys" },
  { value: "other", label: "Other" },
];

const PROCESS_OPTIONS = [
  { value: "manual_portal_check", label: "Checking carrier portals manually" },
  { value: "carrier_emails", label: "Email alerts from carriers" },
  { value: "customer_notified", label: "Notified by our own customers" },
  { value: "other", label: "Other" },
];

export default function TmsOnboardingPage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [tmsName, setTmsName] = useState("");
  const [tmsFreetext, setTmsFreetext] = useState("");
  const [process, setProcess] = useState("");
  const [processOther, setProcessOther] = useState("");
  const [apiAccess, setApiAccess] = useState("");
  const [writeback, setWriteback] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!getToken() || !getAccountId()) {
      navigate("/app");
    }
  }, [navigate]);

  const canSubmit = tmsName && process && apiAccess && writeback !== "" && contactName.trim() && contactEmail.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setError(null);

    try {
      const body = {
        tms_name: tmsName,
        tms_name_freetext: tmsName === "other" ? tmsFreetext.trim() || null : null,
        current_discrepancy_process: process,
        current_process_other: process === "other" ? processOther.trim() || null : null,
        api_access_status: apiAccess,
        writeback_preference: writeback === "yes",
        technical_contact_name: contactName.trim(),
        technical_contact_email: contactEmail.trim(),
      };

      const r = await fetch(`${API}/tms-onboarding/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(body),
      });

      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Submission failed.");

      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={S.page}>
        <div style={S.container}>
          <div style={S.success}>
            <div style={S.successTitle}>Submitted</div>
            <div style={S.successBody}>
              Booksmart will review this request per the response timeline outlined in your service agreement.
            </div>
            <button
              onClick={() => navigate("/app")}
              style={{ ...S.btnPrimary, marginTop: 24, width: "auto", padding: "10px 24px" }}
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <button style={S.backLink} onClick={() => navigate("/app")}>← Dashboard</button>
        <div style={S.title}>TMS Integration</div>
        <div style={S.subtitle}>
          Let us know which TMS you use and how you currently handle carrier discrepancies.
          Booksmart will review and work with you to set up the integration — no configuration needed now.
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div style={S.error}>{error}</div>}

          {/* Step 1 — TMS selection */}
          <div style={S.section}>
            <label style={S.label}>Which TMS do you use?</label>
            <select style={S.select} value={tmsName} onChange={e => setTmsName(e.target.value)}>
              <option value="">— Select —</option>
              {TMS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {tmsName === "other" && (
              <input
                style={{ ...S.input, marginTop: 10 }}
                placeholder="Please tell us the name of your TMS"
                value={tmsFreetext}
                onChange={e => setTmsFreetext(e.target.value)}
              />
            )}
          </div>

          {/* Step 2 — universal questions */}
          <div style={S.section}>
            <label style={S.label}>How do you currently find out about carrier discrepancies?</label>
            <div style={S.radioGroup}>
              {PROCESS_OPTIONS.map(o => (
                <label key={o.value} style={S.radioRow}>
                  <input type="radio" name="process" value={o.value} checked={process === o.value} onChange={e => setProcess(e.target.value)} style={S.radio} />
                  {o.label}
                </label>
              ))}
            </div>
            {process === "other" && (
              <input
                style={{ ...S.input, marginTop: 10 }}
                placeholder="Please describe..."
                value={processOther}
                onChange={e => setProcessOther(e.target.value)}
              />
            )}
          </div>

          <div style={S.section}>
            <label style={S.label}>Does your TMS have API or developer access enabled for your account?</label>
            <div style={{ display: "flex", gap: 24 }}>
              {["yes", "no", "not_sure"].map(v => (
                <label key={v} style={S.radioRow}>
                  <input type="radio" name="apiAccess" value={v} checked={apiAccess === v} onChange={e => setApiAccess(e.target.value)} style={S.radio} />
                  {v === "yes" ? "Yes" : v === "no" ? "No" : "Not sure"}
                </label>
              ))}
            </div>
          </div>

          <div style={S.section}>
            <label style={S.label}>Would you like Booksmart alerts to appear inside your TMS as notes your team can see, or is a separate Booksmart alert enough?</label>
            <div style={{ display: "flex", gap: 24 }}>
              {[
                { value: "yes", label: "Show inside my TMS too" },
                { value: "no", label: "Separate alert is fine" },
              ].map(o => (
                <label key={o.value} style={S.radioRow}>
                  <input type="radio" name="writeback" value={o.value} checked={writeback === o.value} onChange={e => setWriteback(e.target.value)} style={S.radio} />
                  {o.label}
                </label>
              ))}
            </div>
          </div>

          {/* Step 3 — contact info */}
          <div style={S.section}>
            <label style={S.label}>Technical Contact Name</label>
            <input style={S.input} placeholder="Full name" value={contactName} onChange={e => setContactName(e.target.value)} required />
          </div>

          <div style={{ ...S.section, marginBottom: 32 }}>
            <label style={S.label}>Technical Contact Email</label>
            <input style={S.input} type="email" placeholder="you@yourcompany.com" value={contactEmail} onChange={e => setContactEmail(e.target.value)} required />
          </div>

          <button type="submit" style={canSubmit && !loading ? S.btnPrimary : S.btnDisabled} disabled={!canSubmit || loading}>
            {loading ? "Submitting..." : "Submit Request"}
          </button>
        </form>
      </div>
    </div>
  );
}
